import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { EntityType } from '~/enums/EntityType';
import { CreditBalance } from '~/features/billing/CreditBalance/CreditBalance';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import {
  useGetCreditsTooltip,
  useGetEstimatedCreditsTooltip,
} from '../billing/hooks';
import { calculateCreditEstimate } from '../billing/utils';
import { PUBLISH_ELEMENT_ID } from '../constants';
import { ReviewPublishModelVersion } from '../Review/ReviewPublish/ReviewPublishModelVersion';
import { TargetEntityDetails } from '../types/entity';
import { ModelVersion } from '../types/model';
import { testIsNonOptimizedModel } from '../utils/status';

interface Props {
  isVisible: boolean;
  modelVersion: ModelVersion;
  targetEntity: TargetEntityDetails;
  onCancel: () => void;
}

export const PublishModelVersionModal = ({
  isVisible,
  modelVersion,
  targetEntity,
  onCancel,
}: Props) => {
  const [id] = useUuid();
  const [isPublishBtnDisabled, setIsPublishBtndisabled] = useState(true);
  const dispatch = useDispatch();
  const { navigate } = useRouting();
  const { toast } = useToast();
  const intl = useIntl();
  const isDocumentXRayBilling = useFlag(FlagType.DocumentXRayBilling);
  const clientId = useClientId();

  const editModelConfirmed = testIsNonOptimizedModel(modelVersion, [
    'suspended',
  ]);

  const [publishFieldModel, { isLoading: isPublishingFieldModel }] =
    api.endpoints.publishPromptModel.useMutation();

  const {
    data: progress = {
      docsProcessed: 0,
      docsInScope: 0,
    },
    isLoading,
  } = api.endpoints.getPromptModelProgress.useQueryState({
    modelId: modelVersion.modelId,
  });

  const selectPublishMode = useSelector(
    slices.xRay.selectors.selectPublishMode,
  );
  const creditSummary = useSelector(slices.xRay.selectors.selectCreditsSummary);
  const { docsInScope, docsProcessed } = progress;
  const newDocsInScope = calculateCreditEstimate(docsProcessed, docsInScope);

  const credits =
    selectPublishMode === 'new_docs_in_scope' ? newDocsInScope : docsInScope;

  // rescroll back to top if prior scroll effects were triggered
  useEffect(
    () =>
      scrollElementIntoView(PUBLISH_ELEMENT_ID, {
        behavior: 'smooth',
      }),
    [],
  );

  const handlePublish = () => {
    publishFieldModel({
      modelId: modelVersion.modelId,
      version: modelVersion.version,
      idempotencyKey: id,
      publishMode: selectPublishMode,
    })
      .unwrap()
      .then(
        () => {
          toast({
            message: intl.formatMessage(
              {
                id: 'PublishModal.publishSuccessMessage',
                defaultMessage:
                  '"{modelName}" is active and is processing documents.',
                description:
                  'Success message when model is published and processing documents. Example: modelName = "Document Name"',
              },
              {
                modelName: targetEntity.label,
              },
            ),
            status: 'success',
          });
          dispatch(slices.xRay.actions.resetXRay());
          dispatch(
            slices.xRay.actions.setActiveModelResultInfo({
              id: modelVersion.modelId,
              latestVersion: modelVersion.version,
              targetEntityDetails: {
                id: targetEntity.id,
                label: targetEntity.label,
                type: EntityType.DocumentAttachedData,
              },
            }),
          );
          navigate(`${RoutePathType.AutomationHubFields}`);
        },
        () =>
          toast({
            message: intl.formatMessage({
              id: 'PublishModal.publishErrorMessage',
              defaultMessage:
                'There was an error publishing the model. Please try again.',
              description: 'Error message when model publishing fails',
            }),
            status: 'danger',
          }),
      );
  };

  const [publishConfirmationModal, showPublishConfirmationModal] = useModal({
    title: intl.formatMessage({
      id: 'PublishModal.confirmTitle',
      defaultMessage: 'Confirm to Publish',
      description: 'Modal title for confirming model publishing',
    }),
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          <FormattedMessage
            id="PublishModal.publishingMessage"
            defaultMessage="Publishing this model will consume the estimated credits shown below. Please confirm to proceed."
            description="Message explaining credit consumption when publishing model"
          />
        </Text>
        <CreditBalance
          clientId={clientId}
          estimateUnit={{
            modelId: modelVersion.modelId,
            isLoadingUnitValue: isLoading,
            unit: 'document-count',
            value: credits,
          }}
          getCreditsTooltip={useGetCreditsTooltip}
          getEstimatedCreditsTooltip={useGetEstimatedCreditsTooltip}
        />
      </Layout>
    ),
    primaryAction: {
      isLoading: isPublishingFieldModel || isPublishBtnDisabled,
      text: intl.formatMessage({
        id: 'PublishModal.confirmButton',
        defaultMessage: 'Confirm',
        description: 'Confirm button text',
      }),
      onClick: handlePublish,
    },
    onCancel: () => setIsPublishBtndisabled(true),
  });

  const handlePublishConfirmation = () => {
    showPublishConfirmationModal();
    setTimeout(() => {
      setIsPublishBtndisabled(false);
    }, 3000);
  };

  const insufficientCredits =
    editModelConfirmed && creditSummary && creditSummary.credits < credits;

  const primaryAction = {
    disabled: Boolean(insufficientCredits),
    tooltip: insufficientCredits
      ? intl.formatMessage({
          id: 'PublishModal.insufficientCredits',
          defaultMessage: 'You do not have enough credits to publish.',
          description: 'Tooltip when user has insufficient credits',
        })
      : undefined,
    isLoading: isPublishingFieldModel,
    text: intl.formatMessage({
      id: 'PublishModal.publishButton',
      defaultMessage: 'Publish',
      description: 'Publish button text',
    }),
    onClick: isDocumentXRayBilling ? handlePublishConfirmation : handlePublish,
  };

  return (
    <>
      <Modal
        disableHideOnEscape
        headerCalloutContent={
          isDocumentXRayBilling ? (
            <CreditBalance
              clientId={clientId}
              estimateUnit={
                editModelConfirmed
                  ? undefined
                  : {
                      modelId: modelVersion.modelId,
                      isLoadingUnitValue: isLoading,
                      unit: 'document-count',
                      value: credits,
                    }
              }
              getCreditsTooltip={useGetCreditsTooltip}
              getEstimatedCreditsTooltip={useGetEstimatedCreditsTooltip}
            />
          ) : null
        }
        isFullPage
        isVisible={isVisible}
        primaryAction={primaryAction}
        title={intl.formatMessage({
          id: 'PublishModal.title',
          defaultMessage: 'Review & Publish',
          description: 'Modal title for reviewing and publishing model',
        })}
        onCancel={onCancel}
      >
        <Layout
          id={PUBLISH_ELEMENT_ID}
          mx="auto"
          preset="sections"
          w="panel.width.l"
        >
          <StatusMessage
            title={intl.formatMessage({
              id: 'PublishModal.statusMessageTitle',
              defaultMessage: 'Check model overview before publishing',
              description: 'Status message title for model overview check',
            })}
            message={intl.formatMessage({
              id: 'PublishModal.statusMessageContent',
              defaultMessage:
                'The Field Model will save the Field Model Configuration details that you were the most recently on. Once published, the model cannot be modified until it has finished running across the scope of documents you chose.',
              description:
                'Status message content explaining model publishing behavior',
            })}
            status="ai"
            onDismiss={noop}
          />
          <ReviewPublishModelVersion modelVersion={modelVersion} />
        </Layout>
      </Modal>
      {isDocumentXRayBilling && publishConfirmationModal}
    </>
  );
};
